import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWJ324Query } from './hooks/usePWJ324Query';
import type { ResultRow } from '../../services/pwj324Api';
import './PWJ324Form.css';

const PWJ324Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePWJ324Query();
  const [albumFrom, setAlbumFrom] = useState('');
  const [albumTo, setAlbumTo] = useState('');

  const handleQuery = () => {
    runQuery({
      albumFrom: albumFrom.trim() || undefined,
      albumTo: albumTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setAlbumFrom('');
    setAlbumTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>專輯預估成本明細</title>
<style>
body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}
h2{text-align:center;margin-bottom:12px}
table{width:100%;border-collapse:collapse}
th,td{border:1px solid #333;padding:3px 5px}
th{background:#f0f0f0;text-align:center}
.right{text-align:right}
.center{text-align:center}
@media print{body{margin:8mm}}
</style></head><body>
<h2>專輯預估成本明細</h2>
<table>
<thead>
<tr>
<th>專輯代號</th><th>版次</th><th>品名前碼</th><th>品名後碼</th>
<th>幣別</th><th>預估總成本</th><th>項次</th><th>材料類型</th>
<th>品項代號</th><th>規格代號</th><th>預估數量</th><th>單位</th>
<th>預估單價</th><th>供應商代號</th><th>損耗率%</th><th>含損耗用量</th>
<th>組合備考</th>
</tr>
</thead>
<tbody>
${data.map(r => `<tr>
<td>${r.FJ220_01}</td>
<td class="center">${r.FJ220_02}</td>
<td>${r.FJ220_111}</td>
<td>${r.FJ220_112}</td>
<td class="center">${r.FJ220_14}</td>
<td class="right">${r.FJ220_13.toFixed(2)}</td>
<td class="center">${r.FJ221_03}</td>
<td class="center">${r.mat_type_text}</td>
<td>${r.item_code}</td>
<td>${r.FJ221_13}</td>
<td class="right">${r.FJ221_14}</td>
<td class="center">${r.FJ221_15}</td>
<td class="right">${r.FJ221_16.toFixed(4)}</td>
<td>${r.FJ221_17}</td>
<td class="right">${r.FJ221_18}</td>
<td class="right">${r.FJ221_19}</td>
<td>${r.spec_memo}</td>
</tr>`).join('')}
</tbody>
</table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ResultRow> = [
    { title: '專輯代號', dataIndex: 'FJ220_01', key: 'FJ220_01', width: 100, fixed: 'left' },
    { title: '版次', dataIndex: 'FJ220_02', key: 'FJ220_02', width: 55, align: 'center' as const },
    { title: '品名前碼', dataIndex: 'FJ220_111', key: 'FJ220_111', width: 90 },
    { title: '品名後碼', dataIndex: 'FJ220_112', key: 'FJ220_112', width: 90 },
    { title: '幣別', dataIndex: 'FJ220_14', key: 'FJ220_14', width: 55, align: 'center' as const },
    {
      title: '預估總成本', dataIndex: 'FJ220_13', key: 'FJ220_13', width: 110,
      align: 'right' as const,
      render: (v: number) => v != null ? v.toFixed(2) : '',
    },
    { title: '版次備考', dataIndex: 'FJ220_15', key: 'FJ220_15', width: 100, ellipsis: true },
    { title: '項次', dataIndex: 'FJ221_03', key: 'FJ221_03', width: 55, align: 'center' as const },
    { title: '材料類型', dataIndex: 'mat_type_text', key: 'mat_type_text', width: 80, align: 'center' as const },
    { title: '品項代號', dataIndex: 'item_code', key: 'item_code', width: 100 },
    { title: '規格代號', dataIndex: 'FJ221_13', key: 'FJ221_13', width: 90 },
    {
      title: '預估數量', dataIndex: 'FJ221_14', key: 'FJ221_14', width: 90,
      align: 'right' as const,
    },
    { title: '單位', dataIndex: 'FJ221_15', key: 'FJ221_15', width: 55, align: 'center' as const },
    {
      title: '預估單價', dataIndex: 'FJ221_16', key: 'FJ221_16', width: 100,
      align: 'right' as const,
      render: (v: number) => v != null ? v.toFixed(4) : '',
    },
    { title: '供應商代號', dataIndex: 'FJ221_17', key: 'FJ221_17', width: 100 },
    {
      title: '損耗率%', dataIndex: 'FJ221_18', key: 'FJ221_18', width: 75,
      align: 'right' as const,
    },
    {
      title: '含損耗用量', dataIndex: 'FJ221_19', key: 'FJ221_19', width: 100,
      align: 'right' as const,
    },
    { title: '組合備考', dataIndex: 'spec_memo', key: 'spec_memo', ellipsis: true, width: 160 },
  ];

  return (
    <PermissionGuard module={Modules.PWJ324} action="query">
      <div className="pwj324-container">
        <div className="pwj324-header">
          <h2>專輯預估成本明細</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label className="pwj324-label">專輯代號起</label>
              <Input
                value={albumFrom}
                onChange={e => setAlbumFrom(e.target.value)}
                placeholder="起始專輯代號"
                style={{ width: 160 }}
                allowClear
              />
            </div>
            <div>
              <label className="pwj324-label">專輯代號迄</label>
              <Input
                value={albumTo}
                onChange={e => setAlbumTo(e.target.value)}
                placeholder="結束專輯代號"
                style={{ width: 160 }}
                allowClear
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && (
          <Empty description="查無此資料" style={{ marginTop: 40 }} />
        )}
        {queried && !noData && (
          <Table<ResultRow>
            columns={columns}
            dataSource={data}
            rowKey={(row, idx) => `${row.FJ220_01}-${row.FJ220_02}-${row.FJ221_03}-${idx}`}
            loading={loading}
            size="small"
            pagination={{ pageSize: 100, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 1600 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWJ324Form;
